import { TourConfig, TourStep } from '../../types';
import { HABITAT_ONBOARDING_ASLEEP_ID, HABITAT_ONBOARDING_ONLINE_ID, HABITAT_ONBOARDING_OUTAGE_ID } from './ids';
import { VIDEO_DEMO_SRC } from './media';
import { habitatTailSteps, TAIL_STEP_IDS } from './tail';
import { TOUR_TARGETS } from './targets';

const STEP = {
  WELCOME: '1-welcome',
  ZOOM: '2-zoom',
  PAN: '3-pan',
  PAN_FALLBACK_BRANCH: '3-fallback-branch',
  CLIP: '4-clip',
  CLIP_TAKEN: '5-clip-taken',
  CLIP_TAKEN_BRANCH: '5-branch',
  ANGLES: '6-angles',
  FEED: '7-feed',
  FOLLOW_AFTER_SAVE: '9a-follow-after-save',
  VM_ZOOM: 'vm-zoom',
  VM_CLIP: 'vm-clip',
  END: '11-end',
  COMPLETE: 'complete',
} as const;

/**
 * The animals are live on camera. This is the scenario that drives the product's success
 * metric — the user takes a clip or a snapshot — so every branch funnels toward step 4.
 *
 * The video demo appears twice with a distinct step id per caller. Closing it must return to the
 * step that opened it, and a single shared demo step reachable from two parents could not express
 * that without a history pop, which modals do not have.
 */
const steps: TourStep[] = [
  {
    id: STEP.WELCOME,
    analytics: { stepNumber: 1, section: 'Welcome' },
    ui: {
      banner: [
        {
          // 1A — first habitat they have ever opened.
          cond: 'isFirstHabitatVisit',
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            'Hey there! We are LIVE right now from {{ctx.habitat.country}}. That means anything can happen ' +
            "while you're watching. First, do you see an animal from this angle?",
          buttons: [
            { label: 'Yep, I see an animal!', targetStepId: STEP.ZOOM },
            { label: 'Not yet. Where are they?', targetStepId: STEP.PAN },
          ],
        },
        {
          // 1C — they have bought before, so skip the introductions.
          cond: 'isExistingUser',
          type: 'guide',
          mascot: 'talking',
          speechBubble:
            "Hey there, I'm here to help you discover parts of zoolife that may be new to you. " +
            'Looks like we are LIVE from {{ctx.habitat.country}}. Do you see an animal here?',
          buttons: [
            { label: 'Yep, I see an animal!', targetStepId: STEP.ZOOM },
            { label: 'Not yet. Where are they?', targetStepId: STEP.PAN },
          ],
        },
        {
          // 1B — returning without having completed the onboarding.
          type: 'guide',
          mascot: 'talking',
          speechBubble: "Now we're LIVE from {{ctx.habitat.zooName}}! Do you see an animal from here?",
          buttons: [
            { label: 'Yep, I see an animal!', targetStepId: STEP.ZOOM },
            { label: 'Not yet. Where are they?', targetStepId: STEP.PAN },
          ],
        },
      ],
    },
  },

  {
    id: STEP.ZOOM,
    analytics: { stepNumber: 2, section: 'Zoom' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble:
          'Now, you steer the live camera. Look around by tapping where you want the camera to focus, then zoom in!',
        buttons: [
          { label: 'I zoomed in!', targetStepId: STEP.CLIP },
          { label: 'Show me how', targetStepId: STEP.VM_ZOOM },
        ],
      },
      spotlight: { target: TOUR_TARGETS.ZOOM },
      // No auto-advance on the zoom action: zooming in does not mean they found an animal, so
      // moving on is left to the user confirming it with a button.
    },
  },

  {
    id: STEP.PAN,
    analytics: { stepNumber: 3, section: 'Pan' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble:
          "Let's get ready to move the camera! They might be just off to the side, or on the move! Do you see them now?",
        buttons: [
          { label: 'Yep, I see an animal!', targetStepId: STEP.CLIP },
          { label: 'Not yet. Where can they be?', targetStepId: STEP.PAN_FALLBACK_BRANCH },
        ],
      },
      spotlight: { target: TOUR_TARGETS.PAN },
      // Same as zoom: panning around does not prove they found an animal, so advancing is the
      // user's call via the buttons, not automatic on the pan action.
    },
  },

  {
    // With only one camera there are no other angles to try, so fall through to the feed.
    id: STEP.PAN_FALLBACK_BRANCH,
    analytics: { stepNumber: -1 },
    ui: {
      autoAdvance: [
        { cond: 'hasMultipleCameras', delayMs: 0, targetStepId: STEP.ANGLES },
        { delayMs: 0, targetStepId: STEP.FEED },
      ],
    },
  },

  {
    id: STEP.CLIP,
    analytics: { stepNumber: 4, section: 'Clip' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: "Quick! Take a clip of what you're seeing live!",
        buttons: [{ label: 'Show me how', targetStepId: STEP.VM_CLIP }],
      },
      spotlight: { target: TOUR_TARGETS.CAPTURE },
      // The capture arriving (its save modal opening) only moves us on to "now save it" — it is not
      // success. Taking a clip they never keep must not retire anything, so the sibling-retiring
      // completeTours lives on the actual save, one step on.
      onAppEvent: { event: 'habitat:media-captured', targetStepId: STEP.CLIP_TAKEN },
    },
  },

  {
    id: STEP.CLIP_TAKEN,
    analytics: { stepNumber: 5, section: 'Clip taken' },
    // "Now save it" only makes sense while the capture draft is open. A reload drops that draft
    // (it is never persisted), so restoring onto this step would strand the user with nothing to
    // save — send them back to "take a clip" instead. (A live dismiss is handled by media-discarded.)
    restoreTo: STEP.CLIP,
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble:
          "Nice one! Now save it to keep it in your album. You'll also receive likes and comments from the community.",
      },
      // Success is the save, not the capture. Saving retires the sibling scenarios (they only owe the
      // user "prove you can capture", which they now have) and moves this tour on to its own ending.
      // Discarding without saving sends them back to try the capture again rather than stranding them.
      onAppEvent: [
        {
          event: 'habitat:media-saved',
          targetStepId: STEP.CLIP_TAKEN_BRANCH,
          completeTours: [HABITAT_ONBOARDING_ASLEEP_ID, HABITAT_ONBOARDING_OUTAGE_ID],
        },
        { event: 'habitat:media-discarded', targetStepId: STEP.CLIP },
      ],
    },
  },

  {
    // Someone who already follows skips the Follow step and goes straight to the closing beat.
    id: STEP.CLIP_TAKEN_BRANCH,
    analytics: { stepNumber: -1 },
    ui: {
      autoAdvance: [
        { cond: 'isFollowingHabitat', delayMs: 0, targetStepId: STEP.END },
        { delayMs: 0, targetStepId: STEP.FOLLOW_AFTER_SAVE },
      ],
    },
  },

  {
    // The Follow ask on the way out of a save. Authored here rather than reusing the shared tail's
    // Follow step because the two differ in where they lead: the tail's sends the user on to "where
    // next", but a save has already given this tour what it wanted, so this one closes it out. Same
    // reason the video demo is authored twice — one step id per destination.
    id: STEP.FOLLOW_AFTER_SAVE,
    analytics: { stepNumber: 9, section: 'Follow' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: "Click on Follow, and you'll get a ping when they're doing something exciting live on camera!",
      },
      spotlight: { target: TOUR_TARGETS.FOLLOW, scrollIntoView: true },
      onAppEvent: { event: 'habitat:followed', targetStepId: STEP.END },
      // Only reached when they were not following at the branch, but they may follow from elsewhere
      // while sitting here — without this they would be stranded on an ask they have already met.
      autoAdvance: { cond: 'isFollowingHabitat', delayMs: 0, targetStepId: STEP.END },
    },
  },

  {
    id: STEP.ANGLES,
    analytics: { stepNumber: 6, section: 'Angles' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble: "There's more than one camera here. Try switching your point of view to a new angle.",
        buttons: [
          { label: 'There they are!', targetStepId: STEP.ZOOM },
          { label: 'Not yet. Where can they be?', targetStepId: STEP.FEED },
        ],
      },
      spotlight: { target: TOUR_TARGETS.CAMERA_SWITCH },
      onAppEvent: { event: 'habitat:camera-switched', targetStepId: STEP.ZOOM },
    },
  },

  {
    id: STEP.FEED,
    analytics: { stepNumber: 7, section: 'Feed' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'talking',
        speechBubble:
          'Check their Activity Feed to see where they were last spotted. Animals roam, play, nap, and hide on ' +
          'their own schedule.',
        buttons: [
          { label: 'There they are!', targetStepId: STEP.ZOOM },
          { label: 'Who lives here?', targetStepId: TAIL_STEP_IDS.MEET_LEARN },
        ],
      },
      spotlight: { target: TOUR_TARGETS.ACTIVITY_FEED, scrollIntoView: true },
    },
  },

  {
    id: STEP.VM_ZOOM,
    analytics: { stepNumber: -1, section: 'Video demo' },
    ui: { modal: { kind: 'video', src: VIDEO_DEMO_SRC, onCloseStepId: STEP.ZOOM } },
  },
  {
    id: STEP.VM_CLIP,
    analytics: { stepNumber: -1, section: 'Video demo' },
    ui: { modal: { kind: 'video', src: VIDEO_DEMO_SRC, onCloseStepId: STEP.CLIP } },
  },

  {
    id: STEP.END,
    analytics: { stepNumber: 11, section: 'End' },
    ui: {
      banner: {
        type: 'guide',
        mascot: 'idle',
        speechBubble: 'Now keep exploring! Take your time, look around, and see what you can discover.',
      },
      // The banner dismisses itself by completing the tour on the next (bare) step.
      autoAdvance: { delayMs: 6000, targetStepId: STEP.COMPLETE },
    },
  },
  { id: STEP.COMPLETE, completesTour: true, analytics: { stepNumber: 11 }, ui: {} },

  ...habitatTailSteps({ switchHabitatRestarts: true }),
];

export const habitatOnlineConfig: TourConfig = { id: HABITAT_ONBOARDING_ONLINE_ID, steps };
export const ONLINE_FIRST_STEP_ID = STEP.WELCOME;
